/* Typed JSON fetch client. Cookie auth (`cx_session`) via credentials: 'include'.
   Errors come back as ApiError with the server's `{ error: { code, message } }`, or a network error. */
import type { ApiErrorBody, CreateJobRequest, CreateJobResponse } from './api-types';
import { API } from './endpoints';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** The server's `error.details` (conflicting cells, per-field errors), when it sent any. */
  readonly details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (details !== undefined) this.details = details;
  }
  /** True when the request never reached the API (offline, API down, dev proxy failure). */
  get isNetwork(): boolean {
    return this.status === 0 || this.status === 502 || this.status === 503 || this.status === 504;
  }
}

export type Fetcher = typeof fetch;
let fetcher: Fetcher = (...args) => fetch(...args);
/** Tests and demos can swap the transport. */
export function setFetcher(f: Fetcher): void { fetcher = f; }
export function getFetcher(): Fetcher { return fetcher; }

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

function withQuery(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined) qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${path}${path.includes('?') ? '&' : '?'}${s}` : path;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = opts;
  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: { Accept: 'application/json', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...headers },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };
  let res: Response;
  try {
    res = await fetcher(withQuery(path, query), init);
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw err;
    throw new ApiError(0, 'network', 'Could not reach Crosspoint AI +. Check your connection.');
  }
  const text = await res.text();
  let json: unknown = undefined;
  if (text) {
    try { json = JSON.parse(text); } catch { json = undefined; }
  }
  if (!res.ok) {
    const e = (json as ApiErrorBody | undefined)?.error as (ApiErrorBody['error'] & { details?: unknown }) | undefined;
    throw new ApiError(res.status, e?.code ?? `http_${res.status}`, e?.message ?? (res.statusText || 'Request failed'), e?.details);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'PATCH', body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
};

/* Jobs (contract: POST /api/v1/jobs {kind, input} -> {id}). */
export function createJob<I>(kind: string, input: I): Promise<CreateJobResponse> {
  return api.post<CreateJobResponse>(API.jobs, { kind, input } satisfies CreateJobRequest<I>);
}
export function cancelJob(id: string): Promise<unknown> {
  return api.post(API.jobCancel(id));
}
/* Approvals. The API's route is POST /jobs/:id/approve {approvalId, decision: allow | allow_task | deny,
   editedPreview?}; POST /jobs/:id/approvals/:approvalId (the client's words) is an alias kept for older APIs.
   "Allow for the rest of this task" (allow_task) is sent only when the person ticked it and the approval offers
   it (`allowTask` is not false); the server also refuses it (409 allow_task_not_offered) and never lets it
   cover consequential or sensitive steps. */
export type ApprovalOutcomeWord = 'allow' | 'allow_task' | 'deny';

export interface ApprovalExtra {
  /** The person ticked "Allow for the rest of this task". */
  allowForTask?: boolean;
  /** False when the approval does not offer allow_task (from `approval.requested`). */
  allowTask?: boolean;
  editedPreview?: string;
  [k: string]: unknown;
}

/** Client decision words (ApprovalCard) to the API's outcome. */
export function approvalOutcome(decision: string, extra: ApprovalExtra = {}): ApprovalOutcomeWord {
  switch (decision) {
    case 'allow_task':
      return extra.allowTask === false ? 'allow' : 'allow_task';
    case 'approved': case 'approve': case 'edited': case 'allow':
      return extra.allowForTask && extra.allowTask !== false ? 'allow_task' : 'allow';
    // denied, deny, handoff (tier 3: the person does it themselves, so the agent's own step does not run), expired.
    default:
      return 'deny';
  }
}

export async function resolveApproval(jobId: string, approvalId: string, decision: string, extra: ApprovalExtra = {}): Promise<unknown> {
  const outcome = approvalOutcome(decision, extra);
  const body: Record<string, unknown> = { approvalId, decision: outcome };
  if (decision === 'edited' && extra.editedPreview !== undefined) body.editedPreview = extra.editedPreview;
  try {
    return await api.post(API.jobApprove(jobId), body);
  } catch (err) {
    // Only an API without the route (a bare 404/405, not our error envelope) falls back to the alias.
    if (!(err instanceof ApiError) || (err.code !== 'http_404' && err.code !== 'http_405')) throw err;
    return api.post(API.jobApproval(jobId, approvalId), {
      decision: outcome === 'deny' ? 'denied' : decision === 'edited' ? 'edited' : 'approved',
      ...(outcome === 'allow_task' ? { allowForTask: true } : {}),
      ...(body.editedPreview !== undefined ? { editedPreview: body.editedPreview } : {}),
    });
  }
}
